/**
 * @fileoverview API response types and interfaces
 * @module @monorepo/types/api
 *
 * DEPRECATED: This file contains legacy interface-based types.
 * NEW CODE SHOULD USE: Zod schemas from contracts/contract.utils.ts
 *
 * For contract-first design, use:
 * - ApiSuccessResponseSchema, ApiPaginatedResponseSchema from contract.utils
 * - ErrorResponseSchema for NestJS error responses
 * - ApiError, ApiErrorResponse for structured errors
 *
 * @see contracts/contract.utils.ts
 * @see contracts/example.contract.ts (reference implementation)
 */

// Import types from contract.utils (single source of truth)
import type {
	PaginationMeta,
	ApiError,
	ApiErrorResponse as ErrorResponse,
} from './contracts/contract.utils';

// ============================================================================
// Base Response Types
// ============================================================================

/**
 * Standard API Response Structure
 * Used by all endpoints for consistency
 *
 * @template T - The type of data being returned
 *
 * @example
 * ```typescript
 * const response: ApiResponse<User> = {
 *   success: true,
 *   data: { id: '1', name: 'John' },
 *   message: 'User retrieved successfully',
 *   timestamp: '2025-10-10T12:00:00.000Z'
 * };
 * ```
 */
export interface ApiResponse<T = unknown> {
	/** Whether the request was successful */
	readonly success: boolean;
	/** Response data (present on success) */
	readonly data?: T;
	/** Human-readable message */
	readonly message?: string;
	/** Error details (present on failure) */
	readonly error?: ApiError;
	/** ISO 8601 timestamp */
	readonly timestamp: string;
}

/**
 * Success Response
 * For operations that don't return data (e.g., DELETE)
 *
 * @example
 * ```typescript
 * const response: SuccessResponse = {
 *   success: true,
 *   message: 'User deleted successfully',
 *   timestamp: '2025-10-10T12:00:00.000Z'
 * };
 * ```
 */
export interface SuccessResponse {
	readonly success: true;
	readonly message: string;
	readonly timestamp: string;
}

// NOTE: ErrorResponse and ApiError are now defined in contracts/contract.utils.ts
// using Zod schemas for runtime validation. Import from there instead.

/**
 * Validation Error Details
 * For field-level validation errors
 */
export interface ValidationError {
	/** Field name that failed validation */
	readonly field: string;
	/** Validation error message */
	readonly message: string;
	/** Value that failed validation */
	readonly value?: unknown;
	/** Validation constraint that failed */
	readonly constraint?: string;
}

/**
 * Validation Error Response
 * For requests with validation errors
 */
export interface ValidationErrorResponse extends ErrorResponse {
	readonly error: ApiError & {
		readonly code: 'VALIDATION_ERROR';
		readonly validationErrors: readonly ValidationError[];
	};
}

// ============================================================================
// Standard Error Codes
// ============================================================================

/**
 * Standard Error Codes
 * Consistent error codes across all modules
 */
export enum ErrorCode {
	// 4xx Client Errors
	BAD_REQUEST = 'BAD_REQUEST',
	UNAUTHORIZED = 'UNAUTHORIZED',
	FORBIDDEN = 'FORBIDDEN',
	NOT_FOUND = 'NOT_FOUND',
	CONFLICT = 'CONFLICT',
	VALIDATION_ERROR = 'VALIDATION_ERROR',
	UNPROCESSABLE_ENTITY = 'UNPROCESSABLE_ENTITY',
	TOO_MANY_REQUESTS = 'TOO_MANY_REQUESTS',

	// 5xx Server Errors
	INTERNAL_SERVER_ERROR = 'INTERNAL_SERVER_ERROR',
	SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',
	GATEWAY_TIMEOUT = 'GATEWAY_TIMEOUT',

	// Resource Errors
	RESOURCE_NOT_FOUND = 'RESOURCE_NOT_FOUND',
	RESOURCE_ALREADY_EXISTS = 'RESOURCE_ALREADY_EXISTS',
	RESOURCE_LOCKED = 'RESOURCE_LOCKED',
	RESOURCE_EXPIRED = 'RESOURCE_EXPIRED',

	// Business Logic Errors
	INSUFFICIENT_PERMISSIONS = 'INSUFFICIENT_PERMISSIONS',
	OPERATION_NOT_ALLOWED = 'OPERATION_NOT_ALLOWED',
	QUOTA_EXCEEDED = 'QUOTA_EXCEEDED',
	INVALID_STATE = 'INVALID_STATE',

	// External Service Errors
	EXTERNAL_SERVICE_ERROR = 'EXTERNAL_SERVICE_ERROR',
	PAYMENT_ERROR = 'PAYMENT_ERROR',
	EMAIL_SERVICE_ERROR = 'EMAIL_SERVICE_ERROR',
	STORAGE_ERROR = 'STORAGE_ERROR',

	// Session & Auth Errors
	SESSION_EXPIRED = 'SESSION_EXPIRED',
	SESSION_INVALID = 'SESSION_INVALID',
	CSRF_TOKEN_INVALID = 'CSRF_TOKEN_INVALID',
	TOKEN_EXPIRED = 'TOKEN_EXPIRED',
	TOKEN_INVALID = 'TOKEN_INVALID',

	// Network & Client Errors
	NETWORK_ERROR = 'NETWORK_ERROR',
	TIMEOUT = 'TIMEOUT',
	PARSE_ERROR = 'PARSE_ERROR',
	RATE_LIMIT_EXCEEDED = 'RATE_LIMIT_EXCEEDED',
	UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

// ============================================================================
// Pagination Types
// ============================================================================

/**
 * Paginated Response Structure
 * Extends ApiResponse with pagination metadata
 *
 * @template T - The type of items in the array
 *
 * @example
 * ```typescript
 * const response: PaginatedResponse<User> = {
 *   success: true,
 *   data: [{ id: '1', name: 'John' }],
 *   pagination: {
 *     page: 1,
 *     limit: 10,
 *     total: 100,
 *     totalPages: 10,
 *     hasNext: true,
 *     hasPrev: false
 *   },
 *   timestamp: '2025-10-10T12:00:00.000Z'
 * };
 * ```
 */
export interface PaginatedResponse<T> extends ApiResponse<readonly T[]> {
	readonly data: readonly T[];
	readonly meta: PaginationMeta;
}

// ============================================================================
// Bulk Operation Types
// ============================================================================

/**
 * Individual Bulk Operation Failure
 * Details about a single failed item in a bulk operation
 */
export interface BulkOperationFailure {
	/** ID of the item that failed */
	readonly id: string;
	/** Error message */
	readonly error: string;
	/** Error code */
	readonly code?: string;
	/** Additional error details */
	readonly details?: Record<string, unknown>;
}

/**
 * Bulk Operation Result
 * Provides detailed information about bulk operations
 *
 * @example
 * ```typescript
 * const result: BulkOperationResult = {
 *   successCount: 8,
 *   failureCount: 2,
 *   totalCount: 10,
 *   failures: [
 *     { id: '3', error: 'Validation failed', code: 'VALIDATION_ERROR' },
 *     { id: '7', error: 'Already exists', code: 'CONFLICT' }
 *   ],
 *   processingTime: 1234
 * };
 * ```
 */
export interface BulkOperationResult {
	/** Number of successful operations */
	readonly successCount: number;
	/** Number of failed operations */
	readonly failureCount: number;
	/** Total number of operations attempted */
	readonly totalCount: number;
	/** Details of failed operations */
	readonly failures?: readonly BulkOperationFailure[];
	/** Non-critical warnings */
	readonly warnings?: readonly string[];
	/** Processing time in milliseconds */
	readonly processingTime?: number;
}

/**
 * Bulk Operation Response
 * Response for bulk operations
 */
export interface BulkOperationResponse extends ApiResponse<BulkOperationResult> {
	readonly data: BulkOperationResult;
}

// ============================================================================
// Health Check Types
// ============================================================================

/**
 * Health Check Status
 */
export enum HealthStatus {
	HEALTHY = 'healthy',
	DEGRADED = 'degraded',
	UNHEALTHY = 'unhealthy',
	MAINTENANCE = 'maintenance',
}

/**
 * Service Health
 * Health status of a single service/dependency
 */
export interface ServiceHealth {
	/** Service name */
	readonly name: string;
	/** Health status */
	readonly status: HealthStatus;
	/** Response time in milliseconds */
	readonly responseTime?: number;
	/** Additional details */
	readonly details?: Record<string, unknown>;
	/** Error message if unhealthy */
	readonly error?: string;
}

/**
 * Health Check Response
 * Overall system health status
 */
export interface HealthCheckResponse {
	/** Overall system status */
	readonly status: HealthStatus;
	/** Timestamp of health check */
	readonly timestamp: string;
	/** Application version */
	readonly version: string;
	/** Uptime in seconds */
	readonly uptime: number;
	/** Individual service health statuses */
	readonly services: readonly ServiceHealth[];
}

// ============================================================================
// Type Guards
// ============================================================================

/**
 * Type guard to check if response is successful
 */
export function isSuccessResponse<T>(
	response: ApiResponse<T>,
): response is ApiResponse<T> & { success: true; data: T } {
	return response.success === true && response.data !== undefined;
}

/**
 * Type guard to check if response is an error
 */
export function isErrorResponse(response: ApiResponse<unknown>): response is ErrorResponse {
	return response.success === false && response.error !== undefined;
}

/**
 * Type guard to check if response is a validation error
 */
export function isValidationErrorResponse(
	response: ApiResponse<unknown>,
): response is ValidationErrorResponse {
	return (
		isErrorResponse(response) &&
		response.error.code === ErrorCode.VALIDATION_ERROR &&
		'validationErrors' in response.error
	);
}

/**
 * Type guard to check if response is paginated
 */
export function isPaginatedResponse<T>(
	response: ApiResponse<unknown>,
): response is PaginatedResponse<T> {
	return response.success === true && 'pagination' in response && Array.isArray(response.data);
}

// ============================================================================
// Re-export types from contract.utils for backward compatibility
// ============================================================================

export type { ApiError, ErrorResponse } from './contracts/contract.utils';
